import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { convertToUTC } from '../../../lib/convert';
import type { DateRange } from '../../shared';
import { mapRecordToRow } from '../composables/recordMappers';
import { getLabels, getNRecords } from './recordsApi';

export const recordKeys = {
    all: ['records'] as const,
    lists: () => [...recordKeys.all, 'list'] as const,
    list: (start: string, end: string) => [...recordKeys.lists(), start, end] as const,
};

// The backend has no server-side filters or paging for records, so fetch the
// whole range once and let the page filter and paginate in memory.
const FETCH_LIMIT = 1000;

interface RecordRowOptions {
    tempUnit: string;
    // Duration units ({ D, H, M }) in the current language. Memoise it: it's part of
    // `select`, and a new object every render would remap the rows every render.
    translation: { D: string; H: string; M: string };
}

/**
 * Finished records in `range` as table rows.
 *
 * The cache holds the raw API records and `select` maps them to rows, so a change
 * of temperature unit or language remaps what's cached instead of refetching, and
 * mutations can patch a record in the API's own shape.
 */
export const useRecordsQuery = ({ start, end }: DateRange, { tempUnit, translation }: RecordRowOptions) => {
    const startUtc = convertToUTC(start) as string;
    const endUtc = end ? (convertToUTC(end) as string) : 'now';

    const select = useCallback(
        (records: any[]) => {
            const now = dayjs();
            const timeOffset = now.utcOffset();
            return records.map((record, i) =>
                mapRecordToRow(record, { translation, tempUnit, index: i + 1, timeOffset, now })
            );
        },
        [tempUnit, translation]
    );

    return useQuery({
        queryKey: recordKeys.list(startUtc, endUtc),
        queryFn: async (): Promise<any[]> => {
            const to = end ? endUtc : (convertToUTC(dayjs()) as string);
            const data = await getNRecords(0, FETCH_LIMIT, startUtc, to);
            return data?.data?.records || data?.records || [];
        },
        select,
    });
};

export interface LabelMeta {
    id: string;
    name: string;
    color?: string;
}

export type LabelsById = Record<string, LabelMeta>;

export const labelKeys = {
    all: ['labels'] as const,
};

const EMPTY_LABELS: LabelsById = {};

// The labels API has used several field spellings over time; normalise them once.
const toLabelsById = (res: any): LabelsById => {
    const map: LabelsById = {};
    (res?.labels || []).forEach((label: any) => {
        const id = label?.id || label?.label_id || label?.labelId;
        if (!id) return;
        map[id] = {
            id,
            name: label?.name || label?.label_name || label?.labelName || label?.label || id,
            color: label?.color || label?.label_color || label?.labelColor,
        };
    });
    return map;
};

/**
 * The company's labels keyed by id, for rendering label chips in record tables.
 * `mergeLabels` patches the cache after a label is edited in place, so every
 * table showing that label updates without a refetch.
 */
export const useLabelsQuery = () => {
    const queryClient = useQueryClient();
    const { data } = useQuery({
        queryKey: labelKeys.all,
        queryFn: async () => toLabelsById(await getLabels()),
    });

    const mergeLabels = useCallback(
        (updated: LabelMeta[] = []) =>
            queryClient.setQueryData<LabelsById>(labelKeys.all, (prev) => {
                const next = { ...prev };
                updated.forEach((label) => {
                    if (label?.id) next[label.id] = label;
                });
                return next;
            }),
        [queryClient]
    );

    return { labelsById: data ?? EMPTY_LABELS, mergeLabels };
};
